import React, {useContext} from 'react';
import {useRouter} from 'next/router';

import NavigationSubcategory from '~/src/components/navigation/subcategory/NavigationSubcategory';
import Teaser from '~/src/components/teaser/Teaser';
import Link from '~/src/components/link/Link';
import {TeaserLayoutProps} from '~/src/components/teaser/layout/default/TeaserLayoutDefault';

import {SettingsContext} from '~/src/components/context/SettingsContext';
import {getAdUnitName} from '~/src/utilities/helpers/ad';
import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {createFormat} from '~/src/utilities/feed/format';
import {AdFormat} from '~/src/utilities/constants/formats';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {ElementTypes, TeaserTypes} from '~/src/utilities/constants/types';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {getCategoryUrl} from '~/src/utilities/helpers/link';
import {createNavigationTracker} from '~/src/utilities/tracking/gtm';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './TeaserLayoutLifestyle.module.scss';
import i18nStrings from './TeaserLayoutLifestyle.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const TeaserLayoutLifestyle = ({
    children,
    title,
    showTitle = false,
    colorScheme,
    format,
    layout,
    subtype,
    pointerCategoryID,
    fullUrlPath,
    elements
}: TeaserLayoutProps) => {
    const {siteArea} = useContext(SettingsContext);
    const router = useRouter();

    const isCategorySection = subtype === 'category';
    const headerClassNames = classNames(css, 'titleHeader', siteArea, colorScheme, getFormatClassNames(format));
    const teaserClassNames = classNames(css, 'teasers', siteArea, colorScheme, getFormatClassNames(format));

    const teasers = React.Children.toArray(children);

    if (siteArea === SiteArea.LIFESTYLE && !isCategorySection) {
        teasers.unshift(
            <Teaser type={TeaserTypes.AD} key="inside_full_top" adUnitName={getAdUnitName('inside-full-top')} format={createFormat(AdFormat.INSIDE_FULL_TOP)} />
        );
    }

    if (fullUrlPath && router.asPath.includes('lifestyle/front') && !fullUrlPath.includes('front')) {
        const linkIndex = elements[elements.length - 1].type === ElementTypes.AD ? elements.length - 1 : elements.length;
        teasers.splice(
            linkIndex,
            0,
            <Link className={classNames(css, 'moreLifestyle', colorScheme)} href={getCategoryUrl(fullUrlPath)}>
                {getTranslation('moreTo', {channel: title})}
            </Link>
        );
    }

    return (
        <React.Fragment>
            {showTitle && (
                <div className={headerClassNames}>
                    <h1 className={css.title}>
                        {fullUrlPath ? (
                            <Link className={css.titleLink} href={getCategoryUrl(fullUrlPath)} onClick={createNavigationTracker(4, title, pointerCategoryID)}>
                                {title}
                            </Link>
                        ) : (
                            title
                        )}
                    </h1>

                    {layout === SectionLayout.SUB_NAVIGATION && <NavigationSubcategory categoryId={pointerCategoryID} inTeaserSection={true} />}
                </div>
            )}

            <div className={teaserClassNames}>{teasers}</div>
        </React.Fragment>
    );
};

export default TeaserLayoutLifestyle;
